import React, {ReactElement} from 'react';
import DraggableDialog from '../../../../components/DraggableDialog';
import {
    ActiveDialogAvg,
    ActiveDialogGeoJsonFeature,
    ActiveDialogSelectFeature,
    ActiveDialogVectorLayerFeature,
    ActiveDialogVectorLayerFeatureTravelTimeV2,
    DIALOG_INTERFACES,
    DIALOG_INTERFACES_GEO_JSON,
    DIALOG_INTERFACES_VECTOR_LAYER,
    DialogTypes,
    GeoJsonSubTypes,
    VectorLayerSubTypes,
} from '../../../../interfaces/ActiveDialog';
import {GeoJsonPropertiesBridge} from '../../../../interfaces/GeoJsonSources/GeoJsonPropertiesBridge';
import {GeoJsonPropertiesDrip} from '../../../../interfaces/GeoJsonSources/GeoJsonPropertiesDrip';
import {GeoJsonPropertiesRoadWork} from '../../../../interfaces/GeoJsonSources/GeoJsonPropertiesRoadWork';
import {GeoJsonPropertiesSituation} from '../../../../interfaces/GeoJsonSources/GeoJsonPropertiesSituation';
import {
    GeoJsonPropertiesWazeIncident, GeoJsonPropertiesWazeOtherIrregularity, GeoJsonPropertiesWazeOtherTrafficAlert,
    GeoJsonPropertiesWazeOtherTrafficJam
} from '../../../../interfaces/GeoJsonSources/GeoJsonPropertiesWaze';
import {VectorLayerPropertiesFcd} from '../../../../interfaces/VectorLayerSources/VectorLayerPropertiesFcd';
import {WAZE_ALERT_ITEM_SUB_TYPES} from '../../../../interfaces/WazeAlertItemStatus';
import DialogBridge from './components/DialogBridge';
import DialogDfine from './components/DialogDfine';
import DialogDrip from './components/DialogDrip';
import DialogFcd from './components/DialogFcd';
import DialogHere from './components/DialogHere';
import DialogMSI from './components/DialogMSI';
import DialogRoadworkActual from './components/DialogRoadworkActual';
import DialogRoadWorkPlanned from './components/DialogRoadWorkPlanned';
import DialogRushHourLane from './components/DialogRushHourLane';
import DialogSelectFeature from './components/DialogSelectFeature';
import DialogTrafficMessage from './components/DialogTrafficMessage';
import DialogWazeAlertOther from './components/DialogWazeAlertOther';
import DialogWazeAlertIncident from './components/DialogWazeAlertIncident';
import DialogWazeIrregularity from './components/DialogWazeIrregularity';
import DialogWazeTrafficJams from './components/DialogWazeTrafficJams';
import {Props} from './Props';
import {DialogIncidentManagement} from './components/DialogIncidentManagement';
import {
    GeoJsonPropertiesIncidentManagement,
} from '../../../../interfaces/GeoJsonSources/GeoJsonPropertiesIncidentManagement';
import {
    GeoJsonPropertiesRoadWorkPlannedOtm,
} from '../../../../interfaces/GeoJsonSources/GeoJsonPropertiesRoadWorkPlannedOtm';
import {DialogRoadWorkPlannedOtm} from './components/DialogRoadWorkPlannedOtm';
import {GeoJsonPropertiesFd} from '../../../../interfaces/GeoJsonSources/GeoJsonPropertiesFd';
import {DialogFd} from './components/DialogFd';
import {GeoJsonPropertiesSchoolzone} from '../../../../interfaces/GeoJsonSources/GeoJsonPropertiesSchoolzone';
import {DialogSchoolZones} from './components/DialogSchoolZones';
import {DialogueHiddenContent} from './components/DialogueHiddenContent';
import {descriptiveTime, enrichedDescription} from '../../../../services/SituationFeatureService';
import DialogSituationVehicleObstructionEmergencyVehicle
    from './components/DialogSituationVehicleObstructionEmergencyVehicle';
import DialogFlowSpeed from './components/DialogFlowSpeed';
import {
    VectorLayerPropertiesTravelTimeV2,
} from '../../../../interfaces/VectorLayerSources/VectorLayerPropertiesTravelTimeV2';
import DialogTravelTimeV2 from './components/DialogTravelTimeV2';
import DialogSituationOnda from './components/DialogSituationOnda';

const WIDE_DIALOG_WIDTH = 600;
const DEFAULT_DIALOG_WIDTH = 400;

class Dialogs extends React.Component<Props> {
    closeDialog = (id: string | number): void => this.props.requestCloseDialog(id);

    changePinStatus = (index: number, newStatus: boolean): void => {
        this.props.changeDialogPinStatus(index, newStatus);
    };

    clickedOnDialogTitle = (index: number): void => {
        this.props.focusOnDialog(index);
    };

    render(): ReactElement {
        const {activeDialogs} = this.props;

        return <>{activeDialogs.map((item: DIALOG_INTERFACES, index: number) => this.renderDialog(item, index))}</>;
    }

    private renderDialog = (dialog: DIALOG_INTERFACES, index: number) => {
        switch (dialog.type) {
            case DialogTypes.SELECT_FEATURES:
                return this.renderDialogContentSelectFeature(dialog, index);
            case DialogTypes.GEO_JSON:
                return this.renderDialogGeoJSONFeature(dialog, index);
            case DialogTypes.VECTOR_LAYER:
                return this.renderDialogVectorTileFeature(dialog, index);
        }

        return <React.Fragment key={index}/>;
    };

    private renderDialogContentSelectFeature = (item: ActiveDialogSelectFeature, index: number) => {
        const {mapLayers, clickedFeatures} = this.props;

        return (
            <DraggableDialog
                key={item.id}
                pinned={item.pinned}
                startPointX={item.startPosition.x}
                startPointY={item.startPosition.y}
                title={'Kies welke popup u wilt openen'}
                width={DEFAULT_DIALOG_WIDTH}
                zIndex={item.zIndex}
                focusOnDialog={() => this.clickedOnDialogTitle(index)}
                requestDialogClose={() => this.closeDialog(item.id)}
                requestPinStatusChange={() => this.changePinStatus(index, !item.pinned)}
            >
                <p>
                    De positie die u aanklikt heeft meerdere popups die beschikbaar zijn. Kies a.u.b. welke popup u wilt
                    openen uit onderstaande lijst:
                </p>

                <DialogSelectFeature features={item.features} clickedFeatures={clickedFeatures} mapLayers={mapLayers}/>
            </DraggableDialog>
        );
    };

    private renderDialogGeoJSONFeature = (item: DIALOG_INTERFACES_GEO_JSON, index: number) => {
        return (
            <DraggableDialog
                key={`${item.dataSource}-${item.id}`}
                startPointX={item.startPosition.x}
                startPointY={item.startPosition.y}
                pinned={item.pinned}
                title={this.titleForGeoJsonAndVectorPopup(item)}
                zIndex={item.zIndex}
                width={this.getDialogWidth(item)}
                focusOnDialog={() => this.clickedOnDialogTitle(index)}
                requestDialogClose={() => this.closeDialog(item.id)}
                requestPinStatusChange={() => this.changePinStatus(index, !item.pinned)}
            >
                {this.renderGeoJSONPopup(item)}
            </DraggableDialog>
        );
    };

    private readonly getDialogWidth = (item: ActiveDialogGeoJsonFeature | ActiveDialogVectorLayerFeature): number => {
        return item.mapLayer === 'vms-msi' ? WIDE_DIALOG_WIDTH : DEFAULT_DIALOG_WIDTH;
    };

    private renderDialogVectorTileFeature = (item: DIALOG_INTERFACES_VECTOR_LAYER, index: number) => {
        return (
            <DraggableDialog
                key={`${item.dataSource}-${item.id}`}
                startPointX={item.startPosition.x}
                startPointY={item.startPosition.y}
                pinned={item.pinned}
                title={this.titleForGeoJsonAndVectorPopup(item)}
                width={this.getDialogWidth(item)}
                zIndex={item.zIndex}
                focusOnDialog={() => this.clickedOnDialogTitle(index)}
                requestDialogClose={() => this.closeDialog(item.id)}
                requestPinStatusChange={() => this.changePinStatus(index, !item.pinned)}
            >
                {this.renderVectorTilePopup(item)}
            </DraggableDialog>
        );
    };

    private renderGeoJSONPopup = (item: DIALOG_INTERFACES_GEO_JSON) => {
        const props = this.props;
        switch (item.subType) {
            case GeoJsonSubTypes.BRIDGE:
                return (
                    <DialogBridge
                        bridgeHistoryData={item.bridgeHistory}
                        properties={item.currentProperties as never}
                        showRecordIds={props.showRecordIds}
                        onOpenDialog={props.openBridgeGraphDialog}
                    />
                );
            case GeoJsonSubTypes.WAZE_ALERT_INCIDENT:
                return (
                    <DialogWazeAlertIncident
                        currentProperties={item.currentProperties}
                        overriddenRoadInformation={item.overriddenRoadInformation || item.currentProperties.overriddenRoadInformation}
                        isLocked={item.isLocked}
                        status={item.status || item.currentProperties.status}
                        overriddenType={item.overriddenType || (item.currentProperties.overriddenType as WAZE_ALERT_ITEM_SUB_TYPES)}
                        lastChangeTimestamp={item.lastChangeTimestamp}
                        lastSaveTimestamp={item.lastSaveTimestamp}
                        savingStatus={item.savingStatus}
                        timeRemainingBeforeAutomaticClosure={item.timeRemainingBeforeAutomaticClosure}
                        showRecordIds={props.showRecordIds}
                        changeWazeAlertData={props.changeWazeAlertData}
                        openWazeAlertDialog={props.openWazeAlertDialog}
                        closeWazeAlertDialog={props.closeWazeAlertDialog}
                        saveWazeAlertData={props.saveWazeAlertData}
                    />
                );
            case GeoJsonSubTypes.GENERAL:
                switch (item.mapLayer) {
                    case 'drips':
                        return (
                            <DialogDrip
                                currentProperties={item.currentProperties as GeoJsonPropertiesDrip}
                                showRecordIds={props.showRecordIds}
                            />
                        );
                    case 'vms-msi':
                        return (
                            <DialogMSI
                                currentProperties={item.currentProperties as never}
                                showRecordIds={props.showRecordIds}
                            />
                        );
                    case 'vehicleObstructionEmergencyVehicle':
                        return (
                            <DialogSituationVehicleObstructionEmergencyVehicle
                                properties={item.currentProperties as never}
                                showRecordIds={props.showRecordIds}
                            />
                        );
                    case 'here_all':
                    case 'here':
                        return (
                            <DialogHere
                                currentProperties={item.currentProperties as never}
                                showRecordIds={props.showRecordIds}
                            />
                        );
                    case 'safetyRelatedInformation':
                    case 'trafficMessageOtis':
                    case 'trafficMessageOnda':
                        const propertiesOnda = item.currentProperties as GeoJsonPropertiesSituation;
                        const dialogSituationOnda = (
                            <DialogSituationOnda
                                properties={propertiesOnda}
                                showRecordIds={props.showRecordIds}
                            />
                        );
                        return (
                            <DialogueHiddenContent
                                Dialogue={dialogSituationOnda}
                                subTitle={descriptiveTime(propertiesOnda)}
                                description={enrichedDescription(propertiesOnda)}
                            />
                        );
                    case 'trafficMessageFlisvis':
                        const properties = item.currentProperties as GeoJsonPropertiesSituation;
                        const dialogueTrafficMessage = (
                            <DialogTrafficMessage
                                properties={item.currentProperties as never}
                                showRecordIds={props.showRecordIds}
                            />
                        );
                        return (
                            <DialogueHiddenContent
                                Dialogue={dialogueTrafficMessage}
                                subTitle={descriptiveTime(properties)}
                                description={enrichedDescription(properties)}
                            />
                        );
                    case 'roadWorkActual':
                        const dialogueRoadWorkActual = (
                            <DialogRoadworkActual
                                properties={item.currentProperties as never}
                                showRecordIds={props.showRecordIds}
                            />
                        );
                        return (
                            <DialogueHiddenContent
                                Dialogue={dialogueRoadWorkActual}
                                subTitle={descriptiveTime(item.currentProperties as GeoJsonPropertiesSituation)}
                                description={enrichedDescription(item.currentProperties as GeoJsonPropertiesSituation)}
                            />
                        );
                    case 'roadWorkPlanned':
                        return (
                            <DialogRoadWorkPlanned
                                properties={item.currentProperties as never}
                                showRecordIds={props.showRecordIds}
                            />
                        );
                    case 'roadWorkPlannedOtm':
                        return (
                            <DialogRoadWorkPlannedOtm
                                properties={item.currentProperties as never}
                                showRecordIds={props.showRecordIds}
                            />
                        );
                    case 'rushHourLane':
                        const dialogueRushHourLane = (
                            <DialogRushHourLane
                                properties={item.currentProperties as GeoJsonPropertiesSituation}
                                showRecordIds={props.showRecordIds}
                            />
                        );
                        return (
                            <DialogueHiddenContent
                                Dialogue={dialogueRushHourLane}
                                subTitle={descriptiveTime(item.currentProperties as GeoJsonPropertiesSituation)}
                                description={enrichedDescription(item.currentProperties as GeoJsonPropertiesSituation)}
                            />
                        );
                    case 'wazeOtherTrafficAlerts':
                        return (
                            <DialogWazeAlertOther
                                currentProperties={item.currentProperties as never}
                                showRecordIds={props.showRecordIds}
                            />
                        );
                    case 'wazeOtherIrregularity':
                        return (
                            <DialogWazeIrregularity
                                currentProperties={item.currentProperties as never}
                                showRecordIds={props.showRecordIds}
                            />
                        );
                    case 'wazeOtherTrafficJams':
                        return (
                            <DialogWazeTrafficJams
                                currentProperties={item.currentProperties as never}
                                showRecordIds={props.showRecordIds}
                            />
                        );
                    case 'dfine':
                        return (
                            <DialogDfine
                                properties={item.currentProperties as never}
                                showRecordIds={props.showRecordIds}
                            />
                        );
                    case 'im':
                        return (
                            <DialogIncidentManagement
                                properties={item.currentProperties as never}
                                showRecordIds={props.showRecordIds}
                            />
                        );
                    case 'fd':
                        return (
                            <DialogFd
                                properties={item.currentProperties as never}
                                showRecordIds={props.showRecordIds}
                            />
                        );
                    case 'schoolzones':
                        return (
                            <DialogSchoolZones
                                properties={item.currentProperties as never}
                                showRecordIds={props.showRecordIds}
                            />
                        );
                }
        }
        return (
            <>
                {/*If no dialog template is known, display all properties for this element in simple json format. */}
                {JSON.stringify(item.currentProperties, null, 2)}
            </>
        );
    };

    private renderVectorTilePopup = (item: DIALOG_INTERFACES_VECTOR_LAYER) => {
        const {showRecordIds, openGraphDialog} = this.props;

        switch (item.subType) {
            case VectorLayerSubTypes.TRAVEL_TIME_FCD_V2:
            case VectorLayerSubTypes.TRAVEL_TIME_DRIPS_V2:
            case VectorLayerSubTypes.TRAVEL_TIME_OTHER_V2:
                return this.renderVectorLayerTravelTimePopupV2(item);
            case VectorLayerSubTypes.AVG:
                return (<DialogFlowSpeed
                    currentProperties={item.currentProperties as never}
                    currentState={item.currentState as never}
                    graphData={item.graphs}
                    showRecordIds={showRecordIds}
                    openGraphDialog={openGraphDialog}
                />);
            case VectorLayerSubTypes.GENERAL:
                switch (item.mapLayer) {
                    case 'fcdClass':
                        return (
                            <DialogFcd
                                properties={item.currentProperties as never}
                                state={item.currentState as never}
                                showRecordIds={showRecordIds}
                            />
                        );
                }
        }

        return <React.Fragment />;
    };
    private renderVectorLayerTravelTimePopupV2 = (item: ActiveDialogVectorLayerFeatureTravelTimeV2) => {
        const {closeDialog: closeDialogProp, openGraphDialog, showRecordIds} = this.props;

        return (
            <DialogTravelTimeV2
                properties={item.currentProperties as never}
                featureState={item.currentState as never}
                mapSourceID={item.dataSource}
                featureId={item.id as number}
                graphData={item.graphs}
                showRecordIds={showRecordIds}
                closeDialog={closeDialogProp}
                openGraphDialog={openGraphDialog}
            />
        );
    };

    private titleForGeoJsonAndVectorPopup = (item: DIALOG_INTERFACES_GEO_JSON | DIALOG_INTERFACES_VECTOR_LAYER) => {
        switch (item.mapLayer) {
            case 'im':
                const propertiesIm = item.currentProperties as GeoJsonPropertiesIncidentManagement;
                return `IM Geclusterd - ${propertiesIm.id}`;
            case 'bridge':
                const propertiesBridge = item.currentProperties as GeoJsonPropertiesBridge;
                return `Brug ${propertiesBridge.name || propertiesBridge.id}`;
            case 'drips':
                const propertiesDrip = item.currentProperties as GeoJsonPropertiesDrip;
                return `DRIP beeldstand ${propertiesDrip.name}`;
            case 'fcdClass':
                const propertiesFcd = item.currentProperties as VectorLayerPropertiesFcd;
                return `Snelheid o.b.v. FCD ${propertiesFcd.linkId}`;
            case 'avg':
                const propertiesFlowSpeed = item as ActiveDialogAvg;
                return `Lus ${propertiesFlowSpeed.currentState.name || propertiesFlowSpeed.id}`;
            case 'here_all':
            case 'here':
                return 'Sensordata (L2)';
            case 'dfine':
                return 'Sensordata (L3)';
            case 'vms-msi':
                return 'MSI beeldstand';
            case 'vehicleObstructionEmergencyVehicle':
                const propertiesVoev = item.currentProperties as GeoJsonPropertiesSituation;
                return `Botsabsorber/Pijlwagen ${propertiesVoev.id}`;
            case 'safetyRelatedInformation':
            {
                const prop = item.currentProperties as GeoJsonPropertiesSituation;
                return `Veiligheidsgerelateerde melding ${prop.id}`;
            }
            case 'trafficMessageOnda':
                const propertiesCluster = item.currentProperties as GeoJsonPropertiesSituation;
                if (propertiesCluster.primaryLocationDescription) {
                    return `Berging op ${propertiesCluster.primaryLocationDescription} (${propertiesCluster.locationDescription})`;
                } else if (propertiesCluster.secondaryLocationDescription) {
                    return `Berging op ${propertiesCluster.secondaryLocationDescription} (${propertiesCluster.locationDescription})`;
                } else {
                    return `Berging met ID ${propertiesCluster.id}`;
                }
            case 'trafficMessageFlisvis':
                return `RWS Verkeersbericht op ${(item.currentProperties as GeoJsonPropertiesSituation).locationDescription}`;
            case 'roadWorkActual':
                const propertiesRoadWorkActual = item.currentProperties as GeoJsonPropertiesSituation;
                return `Actuele wegwerkzaamheden ${propertiesRoadWorkActual.id}`;
            case 'roadWorkPlanned':
                const propertiesRoadWorkPlanned = item.currentProperties as GeoJsonPropertiesRoadWork;
                return `Geplande wegwerkzaamheden ${propertiesRoadWorkPlanned.id}`;
            case 'rushHourLane':
                const propertiesRushHourLane = item.currentProperties as GeoJsonPropertiesSituation;
                return `Spitsstrook geopend ${propertiesRushHourLane.locationDescription}`;
            case 'trafficMessageOtis':
            case 'traveltime-fcd':
            case 'traveltime-drips':
            case 'traveltime-other':
                const propertiesTravelTime2 = item.currentProperties as VectorLayerPropertiesTravelTimeV2;
                try {
                    const parsed = JSON.parse(propertiesTravelTime2.locations);
                    return parsed[0];
                } catch (e) {
                    return propertiesTravelTime2.externalId;
                }
            case 'wazeIncidents':
                const propertiesWazeAlerts = item.currentProperties as GeoJsonPropertiesWazeIncident;
                return `Waze incident ${propertiesWazeAlerts.address || ''} ${propertiesWazeAlerts.city || ''}`;
            case 'wazeOtherTrafficAlerts':
            {
                const propertiesWazeAlerts = item.currentProperties as GeoJsonPropertiesWazeOtherTrafficAlert;
                return `Waze incident ${propertiesWazeAlerts.address || ''} ${propertiesWazeAlerts.city || ''}`;
            }
            case 'wazeOtherIrregularity':
                const propertiesWazeIrregularity = item.currentProperties as GeoJsonPropertiesWazeOtherIrregularity;
                return `Waze onregelmatigheid ${propertiesWazeIrregularity.address} ${propertiesWazeIrregularity.city}`;
            case 'wazeOtherTrafficJams':
                const propertiesWazeJam = item.currentProperties as GeoJsonPropertiesWazeOtherTrafficJam;
                return `Waze file ${propertiesWazeJam.address} ${propertiesWazeJam.city}`;
            case 'roadWorkPlannedOtm':
                const propertiesRoadWorkOtm = item.currentProperties as GeoJsonPropertiesRoadWorkPlannedOtm;
                return `Geplande wegwerkzaamheden (OTM) - ${propertiesRoadWorkOtm.id}`;
            case 'fd':
                const propertiesFd = item.currentProperties as GeoJsonPropertiesFd;
                return `Ongevalideerde File - ${propertiesFd.headLocation} naar ${propertiesFd.tailLocation}`;
            case 'schoolzones':
                const propertiesSchoolzone = item.currentProperties as GeoJsonPropertiesSchoolzone;
                return `Schoolzone ${propertiesSchoolzone.name}`;
            default:
                return `${item.mapLayer} - ${item.currentProperties!.id}`;
        }
    };
}

export default Dialogs;
